import { useState } from "react";
import ConfirmDialog from "../components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Bell, Search, User } from "lucide-react";
import SonomaLogo from "../assets/sonoma-rounded.png";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { DarkModeToggle } from "@/components/theme/DarkModeToggle";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { signOut, deleteUser } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { auth, db } from "@/firebase/config";
import { setUser } from "@/State/usersSlice";
import { doc, deleteDoc } from "firebase/firestore";
import { useToast } from "@/components/ui/use-toast";

const GuardianHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Search State
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [suggestion, setSuggestion] = useState<{ route: string; label: string; reason: string } | null>(null);

  const handleDeleteAccount = async () => {
    if (confirm("Are you sure you want to permanently delete your account? This cannot be undone.")) {
      const user = auth.currentUser;
      if (user) {
        try {
          setIsDeleting(true);

          // Clear browser storage
          localStorage.clear();
          sessionStorage.clear();

          // Delete Firestore user document
          const userDocRef = doc(db, "users", user.uid);
          await deleteDoc(userDocRef);

          // Delete Firebase Auth user
          await deleteUser(user);

          dispatch(setUser(null));
          toast({
            title: "Account Deleted",
            description: "Your account has been permanently removed.",
            duration: 3000,
          });
          navigate("/guardian-login");
        } catch (error) {
          console.error("Account deletion error:", error);
          toast({
            title: "Deletion Failed",
            description: "An error occurred while deleting your account. Please try again.",
            variant: "destructive",
            duration: 5000,
          });
        } finally {
          setIsDeleting(false);
        }
      }
    }
  };

  const handleSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    setSuggestion(null);

    try {
      // Pass 'guardian' role
      const result = await import("@/services/aiService").then(mod => mod.generateNavigationSuggestion(searchQuery, undefined, 'guardian'));
      
      if (result && result.route) {
        setSuggestion(result);
      } else {
        toast({
            title: "No route found",
            description: "AI could not find a matching page for your request.",
            variant: "destructive"
        });
      }
    } catch (err) {
      console.error("Search error:", err);
      toast({
        title: "Search failed",
        description: "Could not process your request.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleNavigate = () => {
    if (suggestion?.route) {
      navigate(suggestion.route);
      setIsSearchOpen(false);
      setSearchQuery("");
      setSuggestion(null);
    }
  };

  const handleLogout = async () => {
    setShowLogoutDialog(true);
  };

  const confirmLogout = async () => {
    const user = auth.currentUser;
    if (user) {
      try {
        setIsLoggingOut(true);
        // Fully clear all local persistence
        localStorage.clear();
        sessionStorage.clear();
        
        // Remove specific persistence items that might survive clear in some edge cases
        localStorage.removeItem('cachedAuth');
        localStorage.removeItem('portal');
        localStorage.removeItem('userName');
        
        await signOut(auth);
        dispatch(setUser(null));
        
        toast({
          title: "Logged Out",
          description: "You have been successfully logged out. All session data cleared.",
          duration: 3000,
        });
        
        // Use window.location.href for a hard reset to ensure all in-memory states are purged
        window.location.href = "/guardian-login";
      } catch (error) {
        console.error("Logout error:", error);
        toast({
          title: "Logout Failed",
          description: "An error occurred during logout. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setIsLoggingOut(false);
        setShowLogoutDialog(false);
      }
    } else {
      setShowLogoutDialog(false);
    }
  };

  return (
    <>
      <ConfirmDialog
        open={showLogoutDialog}
        title="Log out?"
        description="Are you sure you want to log out?"
        onConfirm={confirmLogout}
        onCancel={() => setShowLogoutDialog(false)}
        confirmText="Log out"
        cancelText="Cancel"
      />

       {/* Search Dialog */}
       {isSearchOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-black/50 backdrop-blur-sm"
             onClick={() => setIsSearchOpen(false)}>
            <div className="w-full max-w-lg bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 p-4 m-4"
                 onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 border-b border-gray-100 dark:border-gray-800 pb-3 mb-3">
                    <Search className="h-5 w-5 text-indigo-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100">Guardian AI Navigation</h3>
                </div>
                
                <form onSubmit={handleSearchSubmit} className="relative">
                    <input
                        autoFocus
                        type="text"
                        placeholder="What do you need? (e.g. 'Pay fees' or 'View grades')"
                        className="w-full p-3 pl-4 rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:ring-2 focus:ring-indigo-500 outline-none transition-all"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <Button 
                        type="submit" 
                        size="sm"
                        disabled={isSearching || !searchQuery.trim()}
                        className="absolute right-2 top-1.5 h-8 w-8 p-0 rounded-full bg-indigo-600 hover:bg-indigo-700 text-white"
                    >
                        {isSearching ? <div className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Search className="h-4 w-4" />}
                    </Button>
                </form>

                {suggestion && (
                    <div className="mt-4 animate-in fade-in slide-in-from-top-2">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider">Suggested Route</p>
                        <div 
                            className="flex items-center justify-between p-3 rounded-lg border border-indigo-100 dark:border-indigo-900/30 bg-indigo-50 dark:bg-indigo-900/10 hover:bg-indigo-100 dark:hover:bg-indigo-900/20 cursor-pointer transition-colors"
                            onClick={handleNavigate}
                        >
                            <div>
                                <h4 className="font-semibold text-indigo-900 dark:text-indigo-100">{suggestion.label}</h4>
                                <p className="text-sm text-indigo-700 dark:text-indigo-300">{suggestion.reason}</p>
                            </div>
                            <Button size="sm" variant="ghost" className="text-indigo-600 dark:text-indigo-400">
                                Go <span className="ml-1">→</span>
                            </Button>
                        </div>
                    </div>
                )}
                
                <div className="mt-4 flex justify-between items-center text-xs text-gray-400 dark:text-gray-500">
                    <span>Powered by Gemini AI</span>
                    <span className="flex gap-1">Press <kbd className="font-mono bg-gray-100 dark:bg-gray-800 px-1 rounded">Enter</kbd> to search</span>
                </div>
            </div>
        </div>
      )}
      <header className="w-full bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-3 sm:px-4 py-2 sm:py-3 flex items-center justify-between">
        <div className="flex items-center space-x-2 sm:space-x-4">
          <SidebarTrigger className="p-1 sm:p-2" />
          <div className="flex items-center">
            <div className="flex items-center justify-center">
              <img
                src={SonomaLogo}
                alt="Sonoma Logo"
                className="h-[22px] w-[22px] sm:h-[32.5px] sm:w-[32.5px] rounded-md"
                style={{
                  display: 'block',
                  boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
                  WebkitFilter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.10))',
                  filter: 'drop-shadow(0 4px 16px rgba(0,0,0,0.18)) contrast(1.2)',
                  background: 'white',
                }}
              />
            </div>
            <div className="flex flex-col leading-tight ml-2 sm:ml-3">
              <span className="text-sm sm:text-lg font-semibold text-gray-800 dark:text-white">
                Sonoma Cloud
              </span>
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                Guardian Portal
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-1 sm:space-x-3">
          <Button
            variant="ghost"
            size="icon"
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
            onClick={() => setIsSearchOpen(true)}
          >
            <Search className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            <Bell className="h-4 w-4" />
          </Button>
          <DarkModeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
              >
                <User className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-56 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
            >
              <DropdownMenuLabel className="text-gray-900 dark:text-white">
                My Account
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
              <DropdownMenuItem className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                Profile Settings
              </DropdownMenuItem>
              <DropdownMenuItem className="text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                Account Preferences
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-gray-200 dark:bg-gray-700" />
              <DropdownMenuItem
                onClick={handleDeleteAccount}
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900"
                disabled={isDeleting}
              >
                {isDeleting ? "Deleting..." : "Delete Account"}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 hover:bg-red-50 dark:hover:bg-red-900"
                disabled={isLoggingOut}
              >
                {isLoggingOut ? "Logging Out..." : "Logout"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>
    </>
  );
};

export default GuardianHeader;
